import { getBozpToken } from '@/api/bozp-token';
import { openTrainingSession, type TrainingPayload, type TrainingSession } from '@/api/training';
import { decodeJwtSub } from '@/lib/jwt';

/**
 * The server-owned training session (decision B2) shared by the training and
 * quiz pages of one hazard profile. Kept in localStorage — DURABLE, not
 * tab-scoped: the server gate only counts chapter events recorded inside ONE
 * session, so a crashed or force-closed browser used to drop the handle and
 * make the worker re-walk every chapter (defect #882). The server's 6h TTL
 * (BOZP_SESSION_TTL_MS), not the tab's lifetime, is what bounds the resume —
 * `ensureTrainingSession` still refuses a handle whose published version
 * changed or whose expiry is near.
 *
 * The key carries the scoped token's subject, exactly like the IndexedDB
 * progress store: a durable handle must never be picked up by the next worker
 * on a shared browser (the API refuses it as NOT_OWNER).
 */
const KEY_PREFIX = 'fixit-bozp:session:';
const inFlight = new Map<string, { profileVersionId: string; promise: Promise<TrainingSession> }>();

function key(hazardProfileId: string): string {
  const workerId = decodeJwtSub(getBozpToken() ?? '') ?? 'anon';
  return `${KEY_PREFIX}${workerId}:${hazardProfileId}`;
}

export function loadTrainingSession(hazardProfileId: string): TrainingSession | null {
  try {
    const raw = localStorage.getItem(key(hazardProfileId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<TrainingSession>;
    if (
      typeof parsed.sessionId !== 'string' ||
      typeof parsed.profileVersionId !== 'string' ||
      typeof parsed.versionHash !== 'string' ||
      typeof parsed.expiresAt !== 'string' ||
      typeof parsed.startedAt !== 'string'
    ) {
      return null;
    }
    return {
      sessionId: parsed.sessionId,
      hazardProfileId,
      profileVersionId: parsed.profileVersionId,
      versionHash: parsed.versionHash,
      startedAt: parsed.startedAt,
      expiresAt: parsed.expiresAt,
    };
  } catch {
    return null;
  }
}

export function clearTrainingSession(hazardProfileId: string): void {
  inFlight.delete(key(hazardProfileId));
  localStorage.removeItem(key(hazardProfileId));
}

/**
 * A usable session for THIS published version: the stored one when it is
 * bound to the same version and not about to expire, otherwise a fresh one
 * from the server. A version change (admin published meanwhile) or an expiry
 * means the chapter events must be re-recorded, so a new session it is.
 */
export async function ensureTrainingSession(
  training: Pick<TrainingPayload, 'id' | 'profileVersionId'>,
  now: number = Date.now(),
): Promise<TrainingSession> {
  // Capture the worker before opening: a new handoff can change the token
  // while the request is pending. Its result still belongs to the first worker.
  const sessionKey = key(training.id);
  const stored = loadTrainingSession(training.id);
  const SAFETY_MS = 60_000;
  if (
    stored &&
    stored.profileVersionId === training.profileVersionId &&
    Date.parse(stored.expiresAt) - SAFETY_MS > now
  ) {
    return stored;
  }
  // StrictMode/remounts can ask concurrently. Both consumers must receive the
  // same session, otherwise a slower opener can replace the quiz's stored ID.
  const pending = inFlight.get(sessionKey);
  if (pending?.profileVersionId === training.profileVersionId) return pending.promise;
  const promise = openTrainingSession(training.id)
    .then((fresh) => {
      // Restart or a newer published version supersedes this request.
      if (inFlight.get(sessionKey)?.promise === promise) {
        localStorage.setItem(sessionKey, JSON.stringify(fresh));
      }
      return fresh;
    })
    .finally(() => {
      if (inFlight.get(sessionKey)?.promise === promise) inFlight.delete(sessionKey);
    });
  inFlight.set(sessionKey, { profileVersionId: training.profileVersionId, promise });
  return promise;
}
